import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, ArrowLeft, Package, Tag, Truck, Calendar } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const CATEGORY_LABELS = {
    estrutura: "Estrutura", cobertura: "Cobertura", equipamento: "Equipamento",
    ferragens: "Ferragens", energia: "Energia", iluminacao: "Iluminação",
    painel_solar: "Painel Solar", acessorio: "Acessório",
};

export default function PublicPartPage() {
    const { partId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/carts/parts/${partId}`);
                setData(data);
            } catch (err) {
                setError(formatApiError(err?.response?.data?.detail) || "Peça não encontrada");
            } finally {
                setLoading(false);
            }
        })();
    }, [partId]);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-muted-foreground">A verificar…</div>;
    }
    if (error || !data?.part) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="max-w-md text-center">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600">
                        <ShieldAlert className="h-7 w-7" />
                    </div>
                    <h1 className="mt-4 font-heading text-2xl font-black text-primary">Peça não encontrada</h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        O código <code className="font-mono">{partId}</code> não corresponde a nenhuma peça registada no sistema NOVIFOOD.
                    </p>
                    <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-bold">
                        <ArrowLeft className="h-4 w-4" /> Ir para novifood.pt
                    </Link>
                </div>
            </div>
        );
    }
    const { part, cart } = data;
    const catLabel = CATEGORY_LABELS[part.category] || part.category;
    const warrantyActive = part.warranty_active;

    return (
        <div className="min-h-screen bg-cream/50">
            {/* Header */}
            <header className="bg-primary text-primary-foreground">
                <div className="mx-auto max-w-2xl px-4 py-6 flex items-center justify-between">
                    <Link to="/" className="font-heading text-2xl font-black tracking-tight">NOVIFOOD</Link>
                    <span className="text-[10px] uppercase tracking-widest text-primary-foreground/70">Verificação</span>
                </div>
            </header>

            <main className="mx-auto max-w-2xl px-4 py-8">
                {/* Warranty status */}
                <section className={`rounded-2xl p-5 border-2 ${warrantyActive ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="warranty-banner">
                    <div className="flex items-start gap-3">
                        <div className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${warrantyActive ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                            {warrantyActive ? <ShieldCheck className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}
                        </div>
                        <div className="flex-1">
                            <h2 className={`font-heading font-black ${warrantyActive ? "text-emerald-900" : "text-amber-900"}`}>
                                {warrantyActive ? "Garantia activa" : "Garantia expirada"}
                            </h2>
                            <p className={`mt-1 text-sm ${warrantyActive ? "text-emerald-900/80" : "text-amber-900/80"}`}>
                                {warrantyActive
                                    ? "Esta peça está coberta pela garantia NOVIFOOD."
                                    : "Contacte a NOVIFOOD para condições de manutenção pós-garantia."}
                            </p>
                            {part.warranty_until && (
                                <p className="mt-1 text-xs font-mono opacity-70">
                                    Válida até {part.warranty_until}
                                </p>
                            )}
                        </div>
                    </div>
                </section>

                {/* Part card */}
                <section className="mt-6 rounded-2xl border border-border bg-white p-6" data-testid="part-card">
                    <div className="flex items-center gap-2 text-primary/60 text-xs uppercase tracking-wider font-bold">
                        <Tag className="h-3.5 w-3.5" />
                        <span>Peça</span>
                        {catLabel && (
                            <span className="ml-auto rounded-full bg-primary/10 text-primary text-[10px] px-2 py-0.5">{catLabel}</span>
                        )}
                    </div>
                    <h1 className="mt-2 font-heading text-2xl font-black text-primary">{part.name}</h1>
                    <div className="mt-1 font-mono text-xs text-muted-foreground tracking-widest">{part.id}</div>

                    <dl className="mt-5 grid gap-4 sm:grid-cols-2 text-sm">
                        {part.sku && <InfoLine label="SKU" value={<span className="font-mono">{part.sku}</span>} />}
                        {part.supplier && <InfoLine label="Fornecedor" value={part.supplier} icon={Truck} />}
                        {part.installed_at && <InfoLine label="Instalada em" value={part.installed_at} icon={Calendar} />}
                        {typeof part.quantity === "number" && <InfoLine label="Quantidade" value={`${part.quantity}×`} />}
                    </dl>
                </section>

                {/* Cart card */}
                {cart && (
                    <section className="mt-4 rounded-2xl border border-border bg-white p-6" data-testid="cart-card">
                        <div className="flex items-center gap-2 text-primary/60 text-xs uppercase tracking-wider font-bold">
                            <Package className="h-3.5 w-3.5" /> Carrinho
                        </div>
                        <h3 className="mt-2 font-heading text-lg font-black text-primary">
                            {cart.nickname || cart.serial_number}
                        </h3>
                        <div className="mt-1 text-xs text-muted-foreground">
                            Modelo <span className="capitalize">{cart.model}</span> · Nº série <span className="font-mono">{cart.serial_number}</span>
                        </div>
                        {cart.reported_lost && (
                            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-900" data-testid="lost-warning">
                                ⚠️ Este carrinho está reportado como <strong>perdido/roubado</strong>. Se o encontrou, contacte a NOVIFOOD.
                            </div>
                        )}
                    </section>
                )}

                <div className="mt-8 text-center">
                    <a href="mailto:contacto@novifood.pt" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-bold hover:-translate-y-0.5 transition">
                        Contactar NOVIFOOD
                    </a>
                    <p className="mt-3 text-[10px] text-muted-foreground">
                        Verificação instantânea via QR · Sistema NOVIFOOD
                    </p>
                </div>
            </main>
        </div>
    );
}

function InfoLine({ label, value, icon: Icon }) {
    return (
        <div className="flex items-start gap-3">
            {Icon && <Icon className="h-4 w-4 text-primary/60 mt-0.5" />}
            <div className="flex-1 min-w-0">
                <dt className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</dt>
                <dd className="mt-0.5 font-bold text-primary truncate">{value}</dd>
            </div>
        </div>
    );
}
